import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { audiologistMasterAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
} from '@phosphor-icons/react';

export default function AudiologistMasterPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formEmp, setFormEmp] = useState('');
  const [formClinic, setFormClinic] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await audiologistMasterAPI.list();
      setList(data.audiologists || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load audiologists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormName(''); setFormEmp(''); setFormClinic(''); setFormActive(true);
    setShowDialog(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setFormName(a.name || '');
    setFormEmp(a.employee_number || '');
    setFormClinic(a.clinic || '');
    setFormActive(a.is_active !== false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await audiologistMasterAPI.update(editing.id, {
          name: formName.trim(), clinic: formClinic, is_active: formActive,
        });
        toast.success('Audiologist updated');
      } else {
        await audiologistMasterAPI.create({
          name: formName.trim(), employee_number: formEmp.trim(), clinic: formClinic.trim(),
        });
        toast.success('Audiologist added');
      }
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await audiologistMasterAPI.remove(id);
      toast.success('Audiologist removed');
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const toggleActive = async (a) => {
    try {
      await audiologistMasterAPI.update(a.id, { is_active: a.is_active === false });
      load();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = list.filter(a =>
    !search ||
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.clinic?.toLowerCase().includes(search.toLowerCase()) ||
    a.employee_number?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allVisibleSelected = filtered.length > 0 && filtered.every(a => selected.includes(a.id));
  const toggleSelectAll = () => setSelected(allVisibleSelected ? [] : filtered.map(a => a.id));
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await audiologistMasterAPI.bulkDelete(selected);
      toast.success(`${data.deleted} audiologist${data.deleted !== 1 ? 's' : ''} removed`);
      setSelected([]); setShowBulkConfirm(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="audiologist-master-page">
      <PageHeader
        title="Audiologist Master"
        subtitle="Add or remove the audiologists shown in the Select Audiologist list on Sales Orders and Invoices (Admin & Clinic)"
      />

      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search audiologists..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="audiologist-search"
            />
          </div>
          <Button onClick={openCreate} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-audiologist-btn">
            <Plus size={16} className="mr-1.5" /> Add Audiologist
          </Button>
          {selected.length > 0 && (
            <Button variant="destructive" onClick={() => setShowBulkConfirm(true)} data-testid="bulk-delete-audiologist-btn">
              <Trash size={16} className="mr-1.5" /> Delete Selected ({selected.length})
            </Button>
          )}
        </div>

        <p className="text-sm text-[#64748B]">{filtered.length} audiologist{filtered.length !== 1 ? 's' : ''}{selected.length > 0 ? ` · ${selected.length} selected` : ''}</p>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="audiologists-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
                <th className="p-3 w-10 text-center">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                    className="rounded border-gray-300 cursor-pointer" data-testid="audiologist-select-all" />
                </th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Employee No.</th>
                <th className="text-left p-3">Clinic</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((a) => (
                  <tr
                    key={a.id}
                    className={`border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors ${selected.includes(a.id) ? 'bg-[#EBF3FA]' : ''}`}
                    data-testid={`audiologist-row-${a.id}`}
                  >
                    <td className="p-3 text-center">
                      <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggleSelect(a.id)}
                        className="rounded border-gray-300 cursor-pointer" data-testid={`select-audiologist-${a.id}`} />
                    </td>
                    <td className="p-3 font-medium text-[#0F172A]">{a.name}</td>
                    <td className="p-3 text-[#64748B]">{a.employee_number || '-'}</td>
                    <td className="p-3 text-[#64748B]">{a.clinic || '-'}</td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => toggleActive(a)}
                        data-testid={`toggle-audiologist-${a.id}`}
                        className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border cursor-pointer ${
                          a.is_active !== false
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-gray-100 text-gray-500 border-gray-200'
                        }`}
                      >
                        {a.is_active !== false ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-audiologist-${a.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteConfirm(a)}
                          data-testid={`delete-audiologist-${a.id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-[#64748B]">
                    {search ? 'No matching audiologists' : 'No audiologists yet. Click "Add Audiologist" to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="audiologist-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">
              {editing ? 'Edit Audiologist' : 'Add Audiologist'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Name *</label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Dr. Priya Sharma" data-testid="audiologist-name-input" />
            </div>
            {!editing && (
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Employee Number</label>
                <Input value={formEmp} onChange={(e) => setFormEmp(e.target.value)}
                  placeholder="Optional (e.g. GOI0063)" data-testid="audiologist-emp-input" />
              </div>
            )}
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Clinic</label>
              <Input value={formClinic} onChange={(e) => setFormClinic(e.target.value)}
                placeholder="Optional" data-testid="audiologist-clinic-input" />
            </div>
            {editing && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)}
                  className="rounded border-gray-300" data-testid="audiologist-active-toggle" />
                <span className="text-sm text-[#0F172A]">Active</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="audiologist-save-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-audiologist-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Remove Audiologist</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B] py-2">
            Remove <strong>{deleteConfirm?.name}</strong> from the audiologist list? They will no longer appear in the Select Audiologist dropdown.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-audiologist-btn">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBulkConfirm} onOpenChange={(o) => { if (!o) setShowBulkConfirm(false); }}>
        <DialogContent className="max-w-sm" data-testid="bulk-delete-audiologist-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Delete Selected Audiologists</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#64748B] py-2">
            Remove <strong>{selected.length}</strong> selected audiologist{selected.length !== 1 ? 's' : ''} from the list? They will no longer appear in the Select Audiologist dropdown.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete-audiologist-btn">
              {bulkDeleting ? 'Deleting...' : `Delete ${selected.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
